<template>
  <Modal
    v-if="TerminateContractModal"
    :is-stepper="true"
    :steps="terminationSteps"
    :objectType="!!isApartment ? 'apartment' : 'parking'"
    v-on:confirm="handleTerminationComplete"
    v-on:cancel="$emit('close')"
    :sendEmail="sendEmail"
    :hasClose="true"
    :hideHeaderButton="true"
    size="lg"
  >
    <template #step-0="{ step }">
      <p class="mb-4">Är det hyresavtal för denna adress du vill säga upp?</p>
      <p v-if="this.typeOfContract === 'apartment' && this.hasParking === true">
        Om du har en bilplats i närliggande område så kommer den att sägas upp tillsammans med
        din bostad.
      </p>
      <div class="relative gap-2 rounded-[15px] bg-gray-10 px-4 py-2 ring-1 ring-gray-light">
        <h3 class="text-b3 font-bold">Adress</h3>
        <p class="text-b3">{{ this.contractAddress }}</p>
      </div>
    </template>

    <template #step-1="{ step }">
      <p class="mb-4">
        Ditt hyresavtal har {{ contractCancelQuantity }} månaders uppsägningstid. Vi tar gärna emot önskemål om tidigare
        avflytt och kontaktar dig i fall det skulle bli aktuellt.
      </p>
      <div
        class="relative flex flex-col gap-3 rounded-[15px] bg-gray-10 px-4 py-2 ring-1 ring-gray-light"
      >
        <div class="flex flex-row justify-between">
          <div class="flex flex-col justify-start">
            <h3 class="text-b3 font-bold">Avflyttningsdatum</h3>
            <p class="text-b3">{{ abgMoveDate }}</p>
          </div>
          <div class="ml-auto flex flex-col items-end">
            <div class="flex flex-col justify-start" v-if="!this.showCalendar">
              <h3 class="text-b3 font-bold px-[10px]" v-if="desiredMoveDate && isntSame">Önskat avflyttningsdatum</h3>
              <p class="text-b3 px-[10px]" v-if="desiredMoveDate && isntSame">{{ formatDate(desiredMoveDate, 'YYYY-MM-DD') }}</p>
            </div>

              <CalendarInput
                v-if="this.showCalendar"
                class="mb-2"
                v-model="desiredMoveDate"
                :format="'YYYY-MM-DD'"
                :lastDayNrInMonths="6"
                :numMonths="contractTerminationMonths"
                @close="showCalendar = false"
              />



            <Button variant="text" size="small" @click="showCalendar = !showCalendar" >
              {{ showCalendar ? 'Stäng datumväljare' : 'Välj ett annat datum' }}
              <template #icon-after>
                <IconEdit />
              </template>
            </Button>
          </div>
        </div>
      </div>
    </template>

    <template #step-2="{ step }">
      <p class="mb-4">
        Vänligen kontrollera att angivet telefonnummer och e-postadress stämmer. Kontaktuppgifter
        för visning anger du vid ett senare tillfälle.
      </p>
      <div
        class="relative flex flex-col gap-3 rounded-[15px] bg-gray-10 px-4 py-2 ring-1 ring-gray-light"
      >
        <div class="flex flex-row">
          <div class="flex flex-col justify-between">
            <h3 class="text-b3 font-bold">Telefonnummer</h3>
            <p class="text-b3">{{phoneMobile}}</p>
          </div>
          <div class="ml-auto flex">
            <Button variant="text" size="small" v-if="!editPhone" @click="editPhone = true;">
              Ändra
              <template #icon-after>
                <IconEdit />
              </template>
            </Button>

            <div v-if="editPhone" class="flex flex-col">
              <Input
                size="sm"
                  name="phoneMobile"
                  type="tel"
                  id="phoneMobile-input"
                  autocomplete="off"
                  placeholder="Telefonnummer"
                  tabindex="0"
                  class="modalInput"
                  v-model="phoneMobile"
                  :disabled="!editPhone" />
            </div>
          </div>
        </div>
        <div class="flex flex-row">
          <div class="flex flex-col justify-between">
            <h3 class="text-b3 font-bold">E-postadress</h3>
            <p class="text-b3">{{email}}</p>
          </div>
          <div class="ml-auto flex">
            <Button variant="text" size="small" v-if="!editEmail" @click="editEmail = true;">
              Ändra
              <template #icon-after>
                <IconEdit />
              </template>
            </Button>

            <div v-if="editEmail" class="flex flex-col">
              <Input
                size="sm"
                name="email"
                type="email"
                id="email-input"
                autocomplete="off"
                placeholder="E-postadress"
                tabindex="0"
                v-model="email"
                :disabled="!editEmail"
                required
              />
            </div>

          </div>
        </div>
      </div>
    </template>

    <template #step-3="{ step }">
      <div class="space-y-4">
        <p>
          Innan uppsägningen registreras behöver den signeras. Detta gör du genom en länk till vår
          e-tjänst Scrive, som vi skickar i ett e-postmeddelande till dig.
        </p>
      </div>
    </template>

    <template #step-4="{ step }">
      <div class="space-y-4">
        <ul class="space-y-2">
          <li class="flex items-start gap-2">
            <IconBullet class="w-4 min-w-4" />
            <p class="mt-1">
              Kontrollera att du mottagit vår länk till din e-postadress och slutför uppsägningen
              genom att följa instruktionerna.
            </p>
          </li>
          <li class="flex items-start gap-2">
            <IconBullet class="w-4 min-w-4" />
            <p class="mt-1">Länken är bara giltig i två timmar.</p>
          </li>
          <li class="flex items-start gap-2">
            <IconBullet class="w-4 min-w-4" />
            <p class="mt-1">
              När du signerat din uppsägning ber vi dig titta på vår checklista med saker att tänka
              på inför din flytt.
            </p>
          </li>
        </ul>
      </div>
    </template>
  </Modal>

</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import * as config from '@/config';

import Box from '@/components/ui/box';
import Button from '@/components/ui/button';
import IconFloorPlan from '@/components/icons/IconFloorPlan';
import IconArrowRight from '@/components/icons/IconArrowRight';
import IconChevronDown from '@/components/icons/IconChevronDown';
import IconExternal from '@/components/icons/IconExternal';
import IconPlus from '@/components/icons/IconPlus';
import IconBullet from '@/components/icons/IconBullet';
import Expandable from '@/components/ui/expandable';
import Modal from '@/components/ui/modal';
import IconEdit from '@/components/icons/IconEdit';
import dayjs from "dayjs";
import CalendarInput from '@/components/ui/calendar-input';
import GeneralMixin from '@/mixins/general';
import Input from '@/components/ui/form/input';

export default {
  name: 'ContractTermination',
  mixins: [GeneralMixin],
  components: {
    Box,
    Button,
    IconFloorPlan,
    IconArrowRight,
    IconChevronDown,
    IconExternal,
    IconPlus,
    Expandable,
    Modal,
    IconEdit,
    IconBullet,
    CalendarInput,
    Input
  },
  props: {
    contractNumber: {
      type: String,
      required: true,
    },
    isApartment: {
      type: Boolean,
    },
    isParking: {
      type: Boolean,
    },
    hasParking: {
      type: Boolean,
    },
    typeOfContract: {
      type: String,
    },
    TerminateContractModal: {
      type: Boolean,
    },

  },
  data() {
    return {
      isLoading: false,
      isError: false,
      isSent: false,
      editEmail: false,
      editPhone: false,
      email: '',
      phoneMobile: null,
      showPolicy: false,
      apiUrl: config.API_BASEURL_V2,
      showCalendar: false,
      terminationSteps: [
        {
          title: 'Säga upp avtal',
          nextButtonText: 'Ja, säg upp avtalet',
          backButtonText: 'Nej, avbryt',
        },
        {
          title: 'När vill du flytta?',
          nextButtonText: 'Nästa',
          backButtonText: 'Tillbaka',
        },
        {
          title: 'Stämmer dina kontaktuppgifter?',
          nextButtonText: 'Nästa',
          backButtonText: 'Tillbaka',
        },
        {
          title: 'Signera din uppsägning',
          nextButtonText: 'Ok, skicka e-postmeddelande',
          backButtonText: 'Tillbaka',
        },
        {
          title: 'Tidsbegränsad länk är skickad till din e-postadress',
          confirmButtonText: 'Gå till checklistan',
          backButtonText: 'Tillbaka',
        },
      ],
    };
  },
  watch: {
    'profile.email': {
      immediate: true,
      handler(email) {
        this.email = email;
      },
    },
    'profile': {
      immediate: true,
      handler(profile) {
        this.phoneMobile = profile.phoneMobile ?? profile.phone;
      },
    },
    desiredMoveDate: 'checkDates',
    contractMoveDate: 'checkDates',
  },

  computed: {
    ...mapGetters(['webapp_config', 'profile']),

    hasEmail() {
      return this.profile && this.profile.email;
    },


    abgMoveDate() {
      return dayjs(this.contractMoveDate.toDate()).format('YYYY-MM-DD');
    },
    showABGMoveDate() {
      return (this.desiredMoveDate != null &&
        dayjs(this.desiredMoveDate).format('YYYY-MM-DD') !=
        dayjs(this.contractMoveDate.toDate()).format('YYYY-MM-DD')
      );
    },
    /**
     * När är uppsägningstiden slut?
     */
    contractTerminationMonths() {
      const contract = this.$store.getters.getContractByContractNumber(this.contractNumber);

      if (!contract.cancelPolicyTenant) return null;

      return contract.cancelPolicyTenant.quantity;
    },

    contractMoveDate() {
      const contract = this.$store.getters.getContractByContractNumber(this.contractNumber);

      if (!contract.cancelPolicyTenant || !contract.cancelPolicyTenant.date) return null;

      return dayjs(contract.cancelPolicyTenant.date)
        .endOf('month');

    },
    contractCancelQuantity() {
      const contract = this.$store.getters.getContractByContractNumber(this.contractNumber);
      return contract.cancelPolicyTenant.quantity;
    },
    contractAddress() {
      const contract = this.$store.getters.getContractByContractNumber(this.contractNumber);
      if (contract && contract.rentalObject) return contract.rentalObject.address1;
      else return "";
    },

    /**
     * Har man valt ett datum tidigare än uppsägningstiden
     */
    desiredMoveDateIsEarlierThanContractMoveDate() {
      if (!this.desiredMoveDate) return;

      return dayjs(this.desiredMoveDate).isBefore(this.contractMoveDate);
    },
  },
  methods: {
    handleTerminationComplete() {
      // Handle the completion of the termination process
      this.TerminateContractModal = false;
      // Navigate to checklist or perform other actions

      this.$emit('close')
    },

    async sendEmail() {
      try {
        this.loading = true;
        await this.$store.dispatch('createTerminationRequest', {
          email: this.email,
          phoneMobile: this.phoneMobile,
          contractNumber: this.contractNumber,
          desiredMoveDate: dayjs(this.desiredMoveDate).format('YYYY-MM-DD'),
        });

        this.loading = false;
        this.$emit('emailSent');
        return 'success';
      } catch (error) {
        this.loading = false;
        this.isError = true;
        return 'error';
      }
    },

    isntSame() {
      if (!this.desiredMoveDate || !this.contractMoveDate) return false;

      const desired = dayjs(this.desiredMoveDate);
      const contract = dayjs(this.contractMoveDate);

      return desired.format('YYYY-MM-DD') !== contract.format('YYYY-MM-DD');

    },
  },

  created() {
    return this.$store.cache.dispatch('fetchProfile').then(() => {
      this.desiredMoveDate = this.contractMoveDate.toDate();
    });
  }
};
</script>
